import { createContext } from 'react';

import type { NotificationContainer_NotificationAppFragment } from '../../NotificationContainer/graphql/NotificationContainer.generated';

export type AppNotification = NotificationContainer_NotificationAppFragment;

export interface NotificationsContext {
  /** All notifications ordered by createdAt DESC */
  notifications: AppNotification[];
  /** Only unread notifications */
  unreadNotifications: AppNotification[];
  /** Complete set of unread validation-question notifications */
  unreadValidationQuestionNotifications: AppNotification[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Notifications indexed by ID for fast lookup */
  byId: Record<string, AppNotification>;
  /** Mark a notification as read. Reports failures by default. */
  markRead: (id: string, options?: { reportErrors?: boolean }) => Promise<void>;
  /** Mark a single notification as unread (client-side only) */
  markUnread: (id: string) => void;
  /** Dismiss a single notification */
  dismiss: (id: string) => Promise<void>;
  /** Dismiss all notifications */
  dismissAll: () => Promise<void>;
  /** Mark all notifications as read */
  markAllRead: () => Promise<void>;
  /** Manually refetch notifications from server */
  refetch: () => void;
  /** Loading state of the initial query */
  loading: boolean;
  /** Whether there are new notifications since last viewed the list */
  hasNewNotifications: boolean;
  /** Update the last viewed notifications list timestamp */
  updateLastViewed: () => Promise<void>;
  /** Load older notifications for pagination */
  loadOlder: () => Promise<void>;
  /** Whether there are more notifications to load */
  hasMore: boolean;
  /** Loading state when fetching older notifications */
  loadingMore: boolean;
  /** Set of source IDs we are waiting on for a generated document download */
  awaitingTargetIdsForDownload: Set<string>;
  /** Add a target ID to await for download */
  addAwaitingTargetIdForDownload: (id: string) => void;
  /** Remove a target ID from the awaiting set */
  removeAwaitingTargetIdForDownload: (id: string) => void;
}

export const NotificationsContext = createContext<
  NotificationsContext | undefined
>(undefined);
